import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { fmtUSDExact } from "@/lib/format";
import { useAuth } from "@/lib/auth";

export default function TradeDialog({ open, onOpenChange, asset, onTraded }) {
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiInsight, setAiInsight] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const { refresh, user } = useAuth();

  const realMode = user?.real_trading_enabled ? user?.real_trading_mode || "paper" : null;
  const isRealLive = realMode === "live";

  React.useEffect(() => {
    if (open) {
      setSide("buy");
      setQty("");
      setAiInsight("");
    }
  }, [open]);

  if (!asset) return null;

  const q = parseFloat(qty) || 0;
  const price = asset.price || 0;
  const gross = q * price;
  const feeCfg = user?.fee_config || { percent: user?.fee_percent ?? 1, min: 0, cap: 0, tiers: [] };
  const effPct = (() => {
    if (user?.is_pro) return feeCfg.pro_percent ?? 0.5;
    let pct = feeCfg.percent ?? 1;
    (feeCfg.tiers || []).forEach((t) => {
      if (gross >= t.min_notional) pct = t.percent;
    });
    return pct;
  })();
  let fee = gross * (effPct / 100);
  if (feeCfg.min > 0 && gross > 0) fee = Math.max(fee, feeCfg.min);
  if (feeCfg.cap > 0) fee = Math.min(fee, feeCfg.cap);
  const total = side === "buy" ? gross + fee : gross - fee;

  const askAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/insight", {
        asset_type: asset.type,
        symbol: asset.symbol,
      });
      setAiInsight(data.insight);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI insight failed");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {    if (q <= 0) {
      toast.error("Enter a quantity");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/trade", {
        asset_type: asset.type,
        symbol: asset.symbol,
        name: asset.name,
        side,
        quantity: q,
      });
      if (data.execution === "real") {
        toast.success(
          `${side.toUpperCase()} ${asset.symbol} · Alpaca ${data.broker_mode} · ${data.order_status}`,
          {
            description:
              data.filled_qty > 0
                ? `Filled ${data.filled_qty} @ ${fmtUSDExact(data.filled_avg_price)}`
                : "Order submitted — check Real Trading for fill status",
          },
        );
      } else {
        toast.success(
          `${side.toUpperCase()} ${q} ${asset.symbol} · fee ${fmtUSDExact(data.fee)}`,
        );
      }
      await refresh();
      onOpenChange(false);
      onTraded?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trade failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="trade-dialog"
        className="bg-neo-surface border border-neo-border rounded-none max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-black tracking-tighter uppercase text-2xl">
            {asset.name}
          </DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            {asset.type} · {asset.symbol}
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="trade-mode-banner"
          className={`mt-2 flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-widest ${
            realMode
              ? isRealLive
                ? "border-[#FF3366]/50 text-[#FF3366] bg-[#FF3366]/5"
                : "border-[#00FF66]/50 text-[#00FF66] bg-[#00FF66]/5"
              : "border-neo-border text-neutral-500"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${realMode ? (isRealLive ? "bg-[#FF3366]" : "bg-[#00FF66]") : "bg-neutral-500"}`} />
          {realMode
            ? isRealLive
              ? "Alpaca live · real money order"
              : "Alpaca paper · real engine, simulated money"
            : "Paper wallet · simulated balance"}
          {realMode && asset.type === "bond" && (
            <span className="ml-auto text-[#FF3366] normal-case tracking-normal">
              bonds not supported in real mode
            </span>
          )}
        </div>

        <div className="mt-4 border border-neo-border p-4 flex items-baseline justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Market price
          </span>
          <span className="font-mono text-2xl font-bold" data-testid="trade-market-price">
            {fmtUSDExact(price, price < 1 ? 4 : 2)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-0 mt-4 border border-neo-border">
          <button
            data-testid="trade-side-buy"
            onClick={() => setSide("buy")}
            className={`py-3 font-mono uppercase tracking-widest text-xs transition-colors ${
              side === "buy" ? "bg-[#00FF66]/10 text-[#00FF66]" : "text-neutral-400 hover:bg-white/5"
            }`}
          >
            Buy
          </button>
          <button
            data-testid="trade-side-sell"
            onClick={() => setSide("sell")}
            className={`py-3 font-mono uppercase tracking-widest text-xs transition-colors border-l border-neo-border ${
              side === "sell" ? "bg-[#FF3366]/10 text-[#FF3366]" : "text-neutral-400 hover:bg-white/5"
            }`}
          >
            Sell
          </button>
        </div>

        <label className="block mt-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Quantity
          </span>
          <input
            data-testid="trade-quantity-input"
            type="number"
            step="any"
            min="0"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="mt-2 w-full bg-transparent border border-neo-border px-4 py-3 font-mono text-lg focus:outline-none focus:border-white transition-colors"
            placeholder="0.00"
          />
        </label>

        <div className="mt-4 border border-neo-border divide-y divide-neo-border font-mono text-sm">
          {realMode ? (
            <>
              <Row label="Est. notional" value={fmtUSDExact(gross)} testid="trade-row-gross" />
              <Row
                label={`Platform fee (${effPct}%${user?.is_pro ? " · PRO" : ""})`}
                value={fmtUSDExact(fee)}
                testid="trade-row-fee"
              />
              <Row
                label="Order type"
                value="Market"
                testid="trade-row-ordertype"
              />
              <Row
                label="Execution"
                value={`Alpaca ${realMode}`}
                bold
                testid="trade-row-total"
              />
            </>
          ) : (
            <>
              <Row label="Gross" value={fmtUSDExact(gross)} testid="trade-row-gross" />
              <Row label={`Fee (${effPct}%${user?.is_pro ? " · PRO" : ""})`} value={fmtUSDExact(fee)} testid="trade-row-fee" />
              <Row
                label={side === "buy" ? "You pay" : "You receive"}
                value={fmtUSDExact(total)}
                bold
                testid="trade-row-total"
              />
            </>
          )}
        </div>

        <div className="mt-4">
          <button
            data-testid="trade-ask-ai"
            onClick={askAI}
            disabled={aiBusy}
            className="w-full flex items-center justify-center gap-2 border border-neo-border py-2.5 font-mono uppercase tracking-widest text-[11px] text-neutral-300 hover:bg-white/5 transition-colors disabled:opacity-40"
          >
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {aiInsight ? "Refresh AI risk read" : "Ask AI — one-tap risk read"}
          </button>
          {aiInsight && (
            <div
              data-testid="trade-ai-insight"
              className="mt-3 border border-neo-border bg-black/30 p-3 text-xs text-neutral-300 whitespace-pre-wrap leading-relaxed max-h-44 overflow-y-auto"
            >
              {aiInsight}
            </div>
          )}
        </div>

        <button
          data-testid="trade-confirm-button"
          onClick={submit}
          disabled={busy || q <= 0}
          className={`mt-6 w-full py-4 font-mono uppercase tracking-widest text-xs border ${
            side === "buy"
              ? "bg-[#00FF66] text-black border-[#00FF66]"
              : "bg-[#FF3366] text-black border-[#FF3366]"
          } hover:opacity-90 transition-opacity disabled:opacity-40`}
        >
          {busy ? "Executing…" : `${side.toUpperCase()} ${asset.symbol}`}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, bold, testid }) {
  return (
    <div className="flex items-center justify-between px-4 py-2" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <span className={bold ? "font-bold text-white" : "text-neutral-200"}>{value}</span>
    </div>
  );
}
